"use client";

import { Calendar, User, ArrowLeft, Share2, Tag } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: { getCurrentWPQueryParams: () => Record<string, any> };

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 w-3/4 rounded-md bg-muted/30 animate-pulse" />
        <div className="h-6 w-1/2 rounded-md bg-muted/30 animate-pulse" />
        <div className="h-96 w-full rounded-lg bg-muted/30 animate-pulse" />
        <div className="space-y-4">
          <div className="h-4 w-full rounded-md bg-muted/30 animate-pulse" />
          <div className="h-4 w-full rounded-md bg-muted/30 animate-pulse" />
          <div className="h-4 w-5/6 rounded-md bg-muted/30 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-muted-foreground">Unable to load this article.</p>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-muted-foreground font-default text-2xl">Article not found.</p>
        <Link to="/HomePage" className="inline-block mt-6">
          <Button variant="outline">
            <ArrowLeft className="size-4" />
            Return Home
          </Button>
        </Link>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorName = post.author?.name || "Nexus Editorial";
  const authorAvatar =
    post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || post.author?.avatar?.["24"];

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-10">
        <Link
          to="/JobBoardPage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="size-4" />
          Back to Insights
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-primary font-medium border border-primary/40 px-3 py-1 rounded-lg"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight text-foreground mb-6">
        {post.title}
      </h1>

      {}
      {post.excerpt && (
        <div
          className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 italic"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="flex flex-wrap items-center gap-6 pb-8 mb-10 border-b border-border">
        <div className="flex items-center gap-3">
          <Avatar className="size-10 border border-border">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="bg-muted text-foreground text-sm">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Written by</p>
            <p className="text-sm font-medium text-foreground">{authorName}</p>
          </div>
        </div>

        {formattedDate && (
          <>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-primary" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Published</p>
                <p className="text-sm font-medium text-foreground">{formattedDate}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="relative mb-12 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none text-foreground/90 leading-relaxed [&>p]:mb-6 [&>p]:text-base md:[&>p]:text-lg [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-semibold [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-10 [&>h3]:mb-3 [&>h3]:text-foreground [&>h4]:font-default [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mt-8 [&>h4]:mb-2 [&>h4]:text-foreground [&>ul]:my-6 [&>ul]:pl-6 [&>ul]:list-disc [&>ul>li]:mb-2 [&>ol]:my-6 [&>ol]:pl-6 [&>ol]:list-decimal [&>ol>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:my-8 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>img]:rounded-lg [&>img]:my-8 [&>img]:w-full [&>pre]:bg-muted [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-6 [&>code]:bg-muted [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded-lg [&>code]:text-sm"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-3">
            <Tag className="size-4 text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">
              Filed Under
            </span>
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="text-xs px-3 py-1 rounded-lg bg-muted text-foreground hover:bg-accent transition-colors"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {}
      <Card className="mt-16 bg-card border border-border shadow-md">
        <CardContent className="px-8 py-2">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <Avatar className="size-20 border-2 border-primary/40">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-muted text-foreground text-xl">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-primary mb-2">About the Author</p>
              <h3 className="font-default text-2xl font-semibold text-foreground mb-3">
                {authorName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Expert recruitment strategist at Nexus Talent Partners, dedicated to connecting top-tier professionals with industry-leading organizations.
              </p>
              <div className="flex items-center gap-3 mt-4">
                <Button variant="outline" size="sm">
                  <User className="size-4" />
                  View Profile
                </Button>
                <Button variant="ghost" size="sm">
                  <Share2 className="size-4" />
                  Share Article
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-4">
            <div className="h-56 rounded-lg bg-muted/30 animate-pulse" />
            <div className="h-6 w-3/4 rounded-md bg-muted/30 animate-pulse" />
            <div className="h-4 w-full rounded-md bg-muted/30 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">No related articles available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => {
        const date = post.date
          ? new Date(post.date).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <article
            data-index={i}
            key={post.id}
            className="group flex flex-col bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-colors shadow-md"
          >
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
            )}
            <div className="flex flex-col flex-1 p-6">
              {post.categories && post.categories.length > 0 && (
                <p className="text-xs uppercase tracking-widest text-primary mb-3">
                  {post.categories[0].name}
                </p>
              )}
              <Link to={post.link || "#"}>
                <h3 className="font-default text-xl font-semibold text-foreground mb-3 leading-snug group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
              </Link>
              {post.excerpt && (
                <div
                  className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              <div className="mt-auto flex items-center justify-between pt-4 border-t border-border">
                {date && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Calendar className="size-3" />
                    {date}
                  </span>
                )}
                <Link to={post.link || "#"}>
                  <Button variant="link" size="sm" className="text-primary px-0">
                    Read More
                  </Button>
                </Link>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: currentWPQueryParams?.post_type || "post",
    per_page: 3,
    orderby: "date",
    order: "DESC",
  });

  return (
    <section data-section-id="4410"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto mt-24 md:mt-32">
          <div className="flex flex-col items-center text-center mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
              Continue Reading
            </span>
            <h2 className="font-default text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Nexus Industry Insights
            </h2>
            <Separator className="w-24 bg-primary/40" />
          </div>

          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}